import { useState, useCallback } from "react";
import { NavLink } from "react-router-dom";

const LANGUAGES = [
  { code: "es", name: "Spanish" },
  { code: "hi", name: "Hindi" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "pt", name: "Portuguese" },
  { code: "ja", name: "Japanese" },
  { code: "zh", name: "Chinese" },
  { code: "ar", name: "Arabic" },
  { code: "ko", name: "Korean" },
  { code: "it", name: "Italian" },
];

const ERROR_CODES = [
  { status: 401, code: "UNAUTHORIZED", desc: "Missing or invalid API key." },
  { status: 402, code: "PAYMENT_REQUIRED", desc: "Insufficient credits." },
  { status: 422, code: "INVALID_LANGUAGE", desc: "Unsupported language code." },
  { status: 422, code: "UNSUPPORTED_FORMAT", desc: "File format not supported or corrupt." },
  { status: 422, code: "FILE_TOO_LARGE", desc: "Source file exceeds 500MB." },
  { status: 429, code: "RATE_LIMITED", desc: "Too many requests. Retry after delay." },
  { status: 500, code: "INTERNAL_ERROR", desc: "Unexpected server error." },
];

function CodeBlock({ code, language = "bash" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative rounded-[12px] bg-card border border-muted-bg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-muted-bg bg-secondary/50">
        <span className="text-xs text-muted font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="text-xs text-muted hover:text-foreground transition-colors"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed bg-[#0f1117]">
        <code className="text-green-300 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

function Badge({ children, color = "blue" }) {
  const colors = {
    blue: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    green: "bg-green-500/15 text-green-400 border-green-500/30",
    yellow: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    red: "bg-red-500/15 text-red-400 border-red-500/30",
    purple: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    gray: "bg-secondary text-muted border-muted-bg",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-medium border ${colors[color]}`}>
      {children}
    </span>
  );
}

function ParamRow({ name, type, required, defaultVal, desc }) {
  return (
    <tr className="border-b border-muted-bg">
      <td className="py-3 pr-4 pl-4 align-top">
        <code className="text-sm text-purple-400 font-mono">{name}</code>
        {required && <span className="ml-1.5 text-xs text-red-400">*</span>}
      </td>
      <td className="py-3 pr-4 align-top">
        <Badge color="gray">{type}</Badge>
      </td>
      <td className="py-3 pr-4 align-top text-sm text-muted font-mono">
        {defaultVal || "—"}
      </td>
      <td className="py-3 pr-4 text-sm text-muted leading-relaxed">{desc}</td>
    </tr>
  );
}

function Section({ id, children }) {
  return (
    <section id={id} className="scroll-mt-6">
      {children}
    </section>
  );
}

const NAV_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "authentication", label: "Authentication" },
  { id: "endpoint-create", label: "POST /api/v1/dub" },
  { id: "endpoint-status", label: "GET /api/v1/dub/:id" },
  { id: "curl-examples", label: "cURL Examples" },
  { id: "try-it", label: "Try It" },
  { id: "cost-estimator", label: "Cost Estimator" },
  { id: "languages", label: "Supported Languages" },
  { id: "errors", label: "Error Codes" },
  { id: "examples", label: "Code Examples" },
];

export default function ApiDocsPage() {
  const [activeTab, setActiveTab] = useState("python");
  const BASE_URL = window.location.origin;

  const CREATE_CURL = `curl -X POST ${BASE_URL}/api/v1/dub \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_url": "https://example.com/video.mp4",
    "target_languages": ["es", "hi", "fr"],
    "voice_type": "stock",
    "lip_sync": false,
    "subtitles": true,
    "webhook_url": "https://yourapp.com/webhook"
  }'`;

  const POLL_CURL = `curl ${BASE_URL}/api/v1/dub/dub_abc123xyz \\
  -H "Authorization: Bearer YOUR_API_KEY"`;

  const CREATE_RESPONSE = `{
  "job_id": "dub_abc123xyz",
  "status": "queued",
  "estimated_time": "5-10 minutes",
  "credits_used": 18.0,
  "target_languages": ["es", "hi", "fr"],
  "created_at": "2026-03-15T10:23:00Z"
}`;

  const POLL_RESPONSE_DONE = `{
  "job_id": "dub_abc123xyz",
  "status": "completed",
  "source_language": "en",
  "created_at": "2026-03-15T10:23:00Z",
  "completed_at": "2026-03-15T10:30:42Z",
  "credits_used": 18.0,
  "outputs": {
    "es": {
      "video_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/es.mp4",
      "subtitle_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/es.srt"
    },
    "hi": {
      "video_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/hi.mp4",
      "subtitle_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/hi.srt"
    },
    "fr": {
      "video_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/fr.mp4",
      "subtitle_url": "https://cdn.dubvid.me/jobs/dub_abc123xyz/fr.srt"
    }
  }
}`;

  const POLL_RESPONSE_FAILED = `{
  "job_id": "dub_abc123xyz",
  "status": "failed",
  "error": {
    "code": "UNSUPPORTED_FORMAT",
    "message": "File format not supported."
  }
}`;

  const ERROR_RESPONSE = `{
  "error": {
    "code": "INVALID_LANGUAGE",
    "message": "Language code 'xx' is not supported.",
    "status": 422
  }
}`;

  const PYTHON_EXAMPLE = `import requests
import time

API_KEY = "YOUR_API_KEY"
BASE_URL = "${BASE_URL}/api/v1"
HEADERS = {"Authorization": f"Bearer {API_KEY}", "Content-Type": "application/json"}

# 1. Submit the job
response = requests.post(f"{BASE_URL}/dub", headers=HEADERS, json={
    "source_url": "https://example.com/video.mp4",
    "target_languages": ["es", "fr"],
    "subtitles": True,
})
job = response.json()
job_id = job["job_id"]
print(f"Job submitted: {job_id}")

# 2. Poll until complete
while True:
    status_res = requests.get(f"{BASE_URL}/dub/{job_id}", headers=HEADERS)
    data = status_res.json()
    print(f"Status: {data['status']}")

    if data["status"] == "completed":
        for lang, output in data["outputs"].items():
            print(f"  {lang}: {output['video_url']}")
        break
    elif data["status"] == "failed":
        print(f"Failed: {data['error']['message']}")
        break

    time.sleep(30)`;

  const NODE_EXAMPLE = `const API_KEY = "YOUR_API_KEY";
const BASE_URL = "${BASE_URL}/api/v1";
const headers = {
  Authorization: \`Bearer \${API_KEY}\`,
  "Content-Type": "application/json",
};

// 1. Submit the job
const res = await fetch(\`\${BASE_URL}/dub\`, {
  method: "POST",
  headers,
  body: JSON.stringify({
    source_url: "https://example.com/video.mp4",
    target_languages: ["es", "fr"],
    subtitles: true,
  }),
});
const { job_id } = await res.json();

// 2. Poll until complete
const poll = async () => {
  const r = await fetch(\`\${BASE_URL}/dub/\${job_id}\`, { headers });
  const data = await r.json();

  if (data.status === "completed") {
    console.log("Outputs:", data.outputs);
  } else if (data.status === "failed") {
    console.error("Failed:", data.error.message);
  } else {
    setTimeout(poll, 30_000); // retry in 30s
  }
};
poll();`;

  return (
    <div className="flex gap-0 -mx-6 -my-10">
      {/* Sidebar */}
      <aside className="hidden lg:block w-52 shrink-0 sticky top-[72px] h-[calc(100vh-72px)] overflow-y-auto border-r border-muted-bg py-8 px-4">
        <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-3 px-3">API Reference</p>
        <nav className="space-y-0.5">
          {NAV_LINKS.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className="block px-3 py-1.5 rounded-[10px] text-sm text-muted hover:text-foreground hover:bg-secondary transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 px-8 py-10 space-y-14">
        {/* Overview */}
        <Section id="overview">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">DubVid API</h1>
            <p className="text-muted text-base leading-relaxed max-w-2xl">
              Programmatically dub videos and audio into 10+ languages using AI voices,
              with optional voice cloning, lip-sync, and subtitle generation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Base URL", value: `${BASE_URL}/api/v1` },
              { label: "Version", value: "v1" },
              { label: "Auth", value: "Bearer token" },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-[12px] border border-muted-bg bg-card px-4 py-3">
                <div className="text-xs text-muted mb-1">{label}</div>
                <code className="text-sm text-foreground font-mono break-all">{value}</code>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-[12px] border border-yellow-500/20 bg-yellow-500/5 p-4">
            <p className="text-sm text-yellow-600 dark:text-yellow-300/80">
              Processing is <strong>asynchronous</strong> — submit a job, then poll{" "}
              <code className="font-mono text-yellow-700 dark:text-yellow-200">GET /api/v1/dub/:id</code> or provide a{" "}
              <code className="font-mono text-yellow-700 dark:text-yellow-200">webhook_url</code> to receive results.
            </p>
          </div>
        </Section>

        {/* Authentication */}
        <Section id="authentication">
          <h2 className="text-xl font-bold text-foreground mb-3">Authentication</h2>
          <p className="text-muted mb-4 leading-relaxed">
            All API requests must include your API key in the{" "}
            <code className="font-mono text-purple-400 text-sm">Authorization</code> header as a Bearer token.
            Generate your key from the{" "}
            <NavLink to="/settings" className="text-primary hover:underline">Settings page</NavLink>.
          </p>
          <CodeBlock
            language="http"
            code={`Authorization: Bearer YOUR_API_KEY`}
          />
        </Section>

        {/* POST /api/v1/dub */}
        <Section id="endpoint-create">
          <div className="flex items-center gap-3 mb-3">
            <Badge color="blue">POST</Badge>
            <code className="text-lg font-mono text-foreground">/api/v1/dub</code>
          </div>
          <p className="text-muted mb-6 leading-relaxed">
            Submit a dubbing job. Provide a publicly accessible URL to your source file and the target languages.
            Returns a <code className="font-mono text-sm text-purple-400">job_id</code> immediately while processing happens in the background.
          </p>

          <h3 className="text-sm font-semibold text-foreground mb-3">Request Body</h3>
          <div className="rounded-[12px] border border-muted-bg overflow-hidden mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-muted-bg bg-secondary">
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Field</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Type</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Default</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: "source_url", type: "string", required: true, def: null, desc: "Publicly accessible URL to the source media file. Supports MP4, MOV, WebM, MP3, WAV. Max 500MB." },
                  { name: "target_languages", type: "string[]", required: true, def: null, desc: 'Array of BCP-47 language codes. E.g. ["es", "hi", "fr"]. Each language produces a separate output.' },
                  { name: "voice_type", type: "string", required: false, def: '"stock"', desc: '"stock" for standard AI voice or "clone" to clone the speaker\'s voice.' },
                  { name: "lip_sync", type: "boolean", required: false, def: "false", desc: "Enable lip-sync to match mouth movements. Best for talking-head videos. Adds +7 credits/min/lang." },
                  { name: "subtitles", type: "boolean", required: false, def: "false", desc: "Generate SRT subtitle files alongside output. Adds +0.2 credits/min/lang." },
                  { name: "webhook_url", type: "string", required: false, def: null, desc: "HTTPS URL to POST results to when job completes. If omitted, poll GET /api/v1/dub/:id." },
                ].map((p) => (
                  <ParamRow key={p.name} name={p.name} type={p.type} required={p.required} defaultVal={p.def} desc={p.desc} />
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-3">Example Request</h3>
          <CodeBlock language="bash" code={CREATE_CURL} />

          <h3 className="text-sm font-semibold text-foreground mt-6 mb-3">
            Response — <Badge color="green">202 Accepted</Badge>
          </h3>
          <CodeBlock language="json" code={CREATE_RESPONSE} />
        </Section>

        {/* GET /api/v1/dub/:id */}
        <Section id="endpoint-status">
          <div className="flex items-center gap-3 mb-3">
            <Badge color="green">GET</Badge>
            <code className="text-lg font-mono text-foreground">/api/v1/dub/:id</code>
          </div>
          <p className="text-muted mb-6 leading-relaxed">
            Poll the status of a dubbing job. When <code className="font-mono text-sm text-purple-400">status</code> is{" "}
            <code className="font-mono text-sm text-green-500">"completed"</code>, the{" "}
            <code className="font-mono text-sm text-purple-400">outputs</code> field contains per-language download URLs.
          </p>

          <h3 className="text-sm font-semibold text-foreground mb-3">Job Status Values</h3>
          <div className="rounded-[12px] border border-muted-bg overflow-hidden mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-muted-bg bg-secondary">
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Status</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { status: "queued", color: "yellow", desc: "Job received, waiting to begin." },
                  { status: "processing", color: "blue", desc: "Actively dubbing." },
                  { status: "completed", color: "green", desc: "Done. Download URLs available in outputs." },
                  { status: "failed", color: "red", desc: "Error occurred. See error.message." },
                ].map(({ status, color, desc }) => (
                  <tr key={status} className="border-b border-muted-bg">
                    <td className="py-3 px-4">
                      <Badge color={color}>{status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-3">Example Request</h3>
          <CodeBlock language="bash" code={POLL_CURL} />

          <h3 className="text-sm font-semibold text-foreground mt-6 mb-3">
            Response — <Badge color="green">200 OK</Badge> (completed)
          </h3>
          <CodeBlock language="json" code={POLL_RESPONSE_DONE} />

          <h3 className="text-sm font-semibold text-foreground mt-6 mb-3">
            Response — <Badge color="green">200 OK</Badge> (failed)
          </h3>
          <CodeBlock language="json" code={POLL_RESPONSE_FAILED} />

          <div className="mt-4 rounded-[12px] border border-muted-bg bg-card p-4">
            <p className="text-sm text-muted">
              Recommended polling interval: <strong className="text-foreground">every 30–60 seconds</strong> while
              status is <code className="font-mono text-yellow-500">"processing"</code>.
            </p>
          </div>
        </Section>

        {/* cURL Examples */}
        <Section id="curl-examples">
          <h2 className="text-xl font-bold text-foreground mb-2">cURL Examples</h2>
          <p className="text-muted mb-6 leading-relaxed">
            Ready-to-run commands against <code className="font-mono text-sm text-primary">{BASE_URL}</code>. Replace <code className="font-mono text-sm text-purple-400">YOUR_API_KEY</code> with a key from{" "}
            <NavLink to="/settings" className="text-primary hover:underline">Settings</NavLink>.
          </p>

          <h3 className="text-sm font-semibold text-foreground mb-3">Submit a dubbing job</h3>
          <CodeBlock language="bash" code={CREATE_CURL} />

          <h3 className="text-sm font-semibold text-foreground mt-6 mb-3">Check job status</h3>
          <CodeBlock language="bash" code={POLL_CURL} />

          <h3 className="text-sm font-semibold text-foreground mt-6 mb-3">With voice cloning + lip-sync</h3>
          <CodeBlock
            language="bash"
            code={`curl -X POST ${BASE_URL}/api/v1/dub \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_url": "https://example.com/video.mp4",
    "target_languages": ["es"],
    "voice_type": "clone",
    "lip_sync": true,
    "subtitles": true
  }'`}
          />
        </Section>

        {/* Try It */}
        <Section id="try-it">
          <h2 className="text-xl font-bold text-foreground mb-2">Try It</h2>
          <p className="text-muted mb-6 leading-relaxed">
            Test the API directly from your browser. Enter your API key and fill in the request fields below.
          </p>
          <TryItPanel />
        </Section>

        {/* Cost Estimator */}
        <Section id="cost-estimator">
          <h2 className="text-xl font-bold text-foreground mb-3">Cost Estimator</h2>
          <p className="text-muted mb-6 leading-relaxed">
            Calculate the credit cost before submitting a job. This is a client-side formula — no API call required.
          </p>

          <div className="rounded-[12px] border border-muted-bg overflow-hidden mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-muted-bg bg-secondary">
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Feature</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Rate</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">USD</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Base", rate: "1 credit / min / language", usd: "$0.30/credit" },
                  { feature: "Voice Clone", rate: "+1 credit / min / language", usd: "+$0.30/credit" },
                  { feature: "Subtitles", rate: "+0.2 credits / min / language", usd: "+$0.06/credit" },
                  { feature: "Lip-Sync", rate: "+7 credits / min / language", usd: "+$2.10/credit" },
                  { feature: "Handling Fee", rate: "flat per job", usd: "$0.50" },
                ].map(({ feature, rate, usd }) => (
                  <tr key={feature} className="border-b border-muted-bg">
                    <td className="py-3 px-4 text-sm font-medium text-foreground">{feature}</td>
                    <td className="py-3 px-4 text-sm text-muted font-mono">{rate}</td>
                    <td className="py-3 px-4 text-sm text-muted">{usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-3">Formula</h3>
          <CodeBlock
            language="javascript"
            code={`function estimateCost({ durationMin, numLanguages, voiceType, lipSync, subtitles }) {
  const base     = durationMin * numLanguages;
  const clone    = voiceType === "clone"  ? durationMin * numLanguages * 1   : 0;
  const subs     = subtitles             ? durationMin * numLanguages * 0.2 : 0;
  const lipsync  = lipSync               ? durationMin * numLanguages * 7   : 0;

  const totalCredits = base + clone + subs + lipsync;
  const costUsd      = (totalCredits * 0.30) + 0.50;  // $0.50 flat handling fee

  return { totalCredits, costUsd };
}

// Example: 5 min · 3 languages · stock voice · subtitles on
// base=15, subs=3 → 18 credits → $5.90`}
          />

          <CostCalculator />
        </Section>

        {/* Languages */}
        <Section id="languages">
          <h2 className="text-xl font-bold text-foreground mb-4">Supported Languages</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {LANGUAGES.map(({ code, name }) => (
              <div key={code} className="rounded-[12px] border border-muted-bg bg-card px-4 py-3">
                <code className="block text-sm font-mono text-purple-400 mb-1">{code}</code>
                <span className="text-sm text-muted">{name}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Error Codes */}
        <Section id="errors">
          <h2 className="text-xl font-bold text-foreground mb-3">Error Codes</h2>
          <p className="text-muted mb-6">All errors return JSON with <code className="font-mono text-sm text-purple-400">error.code</code>, <code className="font-mono text-sm text-purple-400">error.message</code>, and <code className="font-mono text-sm text-purple-400">error.status</code>.</p>

          <CodeBlock language="json" code={ERROR_RESPONSE} />

          <div className="mt-6 rounded-[12px] border border-muted-bg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-muted-bg bg-secondary">
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">HTTP Status</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Code</th>
                  <th className="text-left py-2.5 px-4 text-xs font-medium text-muted">Description</th>
                </tr>
              </thead>
              <tbody>
                {ERROR_CODES.map(({ status, code, desc }) => (
                  <tr key={code} className="border-b border-muted-bg">
                    <td className="py-3 px-4">
                      <Badge color={status >= 500 ? "red" : status >= 400 ? "yellow" : "green"}>{status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm font-mono text-red-400">{code}</code>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Code Examples */}
        <Section id="examples">
          <h2 className="text-xl font-bold text-foreground mb-4">Code Examples</h2>

          <div className="flex gap-2 mb-4">
            {["python", "node"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-[10px] text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "bg-primary/10 text-primary"
                    : "text-muted hover:text-foreground hover:bg-secondary"
                }`}
              >
                {tab === "python" ? "Python" : "Node.js"}
              </button>
            ))}
          </div>

          {activeTab === "python" && <CodeBlock language="python" code={PYTHON_EXAMPLE} />}
          {activeTab === "node" && <CodeBlock language="javascript" code={NODE_EXAMPLE} />}
        </Section>
      </main>
    </div>
  );
}

function TryItPanel() {
  const BASE = window.location.origin;
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("create");

  const [sourceUrl, setSourceUrl] = useState("");
  const [langs, setLangs] = useState("es");
  const [voiceType, setVoiceType] = useState("stock");
  const [lipSync, setLipSync] = useState(false);
  const [subtitles, setSubtitles] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  const [jobId, setJobId] = useState("");

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [statusCode, setStatusCode] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setResponse(null);
    setStatusCode(null);

    try {
      let res;
      if (endpoint === "create") {
        const body = {
          source_url: sourceUrl,
          target_languages: langs.split(",").map((l) => l.trim()).filter(Boolean),
          voice_type: voiceType,
          lip_sync: lipSync,
          subtitles: subtitles,
        };
        if (webhookUrl) body.webhook_url = webhookUrl;

        res = await fetch(`${BASE}/api/v1/dub`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify(body),
        });
      } else {
        const id = jobId.trim();
        res = await fetch(`${BASE}/api/v1/dub/${id}`, {
          headers: { Authorization: `Bearer ${apiKey}` },
        });
      }

      setStatusCode(res.status);
      const json = await res.json();
      setResponse(json);
    } catch (err) {
      setResponse({ error: err.message });
    } finally {
      setLoading(false);
    }
  }, [apiKey, endpoint, sourceUrl, langs, voiceType, lipSync, subtitles, webhookUrl, jobId]);

  const statusColor = statusCode
    ? statusCode < 300 ? "text-green-500" : statusCode < 500 ? "text-yellow-500" : "text-red-500"
    : "";

  const inputCls = "w-full bg-secondary border border-muted-bg rounded-[10px] px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary";
  const labelCls = "text-xs text-muted block mb-1";

  return (
    <div className="rounded-[16px] border border-muted-bg overflow-hidden bg-card">
      {/* Header */}
      <div className="px-6 py-4 border-b border-muted-bg bg-secondary flex flex-wrap items-end gap-4">
        <label className="flex-1 min-w-48">
          <span className={labelCls}>API Key</span>
          <input
            type="password"
            placeholder="dv_..."
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className={inputCls}
          />
        </label>
        <label>
          <span className={labelCls}>Endpoint</span>
          <select
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            className="bg-secondary border border-muted-bg rounded-[10px] px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
          >
            <option value="create">POST /api/v1/dub</option>
            <option value="status">GET /api/v1/dub/:id</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-muted-bg">
        {/* Left: inputs */}
        <div className="px-6 py-5 space-y-4">
          <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">Request</h3>

          {endpoint === "create" ? (
            <>
              <label className="block">
                <span className={labelCls}>source_url <span className="text-red-400">*</span></span>
                <input
                  type="url"
                  placeholder="https://example.com/video.mp4"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>target_languages (comma-separated)</span>
                <input
                  type="text"
                  placeholder="es, fr, de"
                  value={langs}
                  onChange={(e) => setLangs(e.target.value)}
                  className={`${inputCls} font-mono`}
                />
              </label>
              <label className="block">
                <span className={labelCls}>voice_type</span>
                <select
                  value={voiceType}
                  onChange={(e) => setVoiceType(e.target.value)}
                  className={inputCls}
                >
                  <option value="stock">stock</option>
                  <option value="clone">clone</option>
                </select>
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={subtitles} onChange={(e) => setSubtitles(e.target.checked)} className="accent-primary" />
                  <span className="text-sm text-muted">subtitles</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={lipSync} onChange={(e) => setLipSync(e.target.checked)} className="accent-primary" />
                  <span className="text-sm text-muted">lip_sync</span>
                </label>
              </div>
              <label className="block">
                <span className={labelCls}>webhook_url (optional)</span>
                <input
                  type="url"
                  placeholder="https://yourapp.com/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className={inputCls}
                />
              </label>
            </>
          ) : (
            <label className="block">
              <span className={labelCls}>job_id <span className="text-red-400">*</span></span>
              <input
                type="text"
                placeholder="dub_123"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                className={`${inputCls} font-mono`}
              />
            </label>
          )}

          <button
            onClick={run}
            disabled={loading || !apiKey}
            className="w-full py-2.5 bg-primary text-white rounded-[10px] text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? "Sending..." : "Send Request"}
          </button>
        </div>

        {/* Right: response */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">Response</h3>
            {statusCode && (
              <span className={`text-sm font-mono font-bold ${statusColor}`}>HTTP {statusCode}</span>
            )}
          </div>
          {response ? (
            <pre className="bg-[#0f1117] rounded-[10px] p-4 text-xs font-mono text-green-300 overflow-auto max-h-80 whitespace-pre-wrap">
              {JSON.stringify(response, null, 2)}
            </pre>
          ) : (
            <div className="bg-secondary rounded-[10px] p-4 text-sm text-muted italic">
              {loading ? "Waiting for response..." : "Response will appear here"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CostCalculator() {
  const [duration, setDuration] = useState(5);
  const [languages, setLanguages] = useState(3);
  const [voiceType, setVoiceType] = useState("stock");
  const [lipSync, setLipSync] = useState(false);
  const [subtitles, setSubtitles] = useState(false);

  const base = duration * languages;
  const clone = voiceType === "clone" ? duration * languages * 1 : 0;
  const subs = subtitles ? duration * languages * 0.2 : 0;
  const ls = lipSync ? duration * languages * 7 : 0;
  const total = base + clone + subs + ls;
  const costUsd = (total * 0.3 + 0.5).toFixed(2);

  const inputCls = "w-full bg-secondary border border-muted-bg rounded-[10px] px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary";

  return (
    <div className="mt-6 rounded-[16px] border border-muted-bg bg-card p-6">
      <h3 className="text-sm font-semibold text-foreground mb-5">Interactive Calculator</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <label className="block">
          <span className="text-xs text-muted mb-1.5 block">Duration (minutes)</span>
          <input
            type="number"
            min={1}
            max={10}
            value={duration}
            onChange={(e) => setDuration(Math.max(1, Number(e.target.value)))}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted mb-1.5 block">Number of languages</span>
          <input
            type="number"
            min={1}
            max={10}
            value={languages}
            onChange={(e) => setLanguages(Math.max(1, Math.min(10, Number(e.target.value))))}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted mb-1.5 block">Voice type</span>
          <select
            value={voiceType}
            onChange={(e) => setVoiceType(e.target.value)}
            className={inputCls}
          >
            <option value="stock">Stock</option>
            <option value="clone">Clone</option>
          </select>
        </label>
        <div className="flex gap-6 items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={subtitles}
              onChange={(e) => setSubtitles(e.target.checked)}
              className="accent-primary"
            />
            <span className="text-sm text-muted">Subtitles</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={lipSync}
              onChange={(e) => setLipSync(e.target.checked)}
              className="accent-primary"
            />
            <span className="text-sm text-muted">Lip-sync</span>
          </label>
        </div>
      </div>

      <div className="border-t border-muted-bg pt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Base credits", value: base.toFixed(1) },
          { label: "Add-on credits", value: (clone + subs + ls).toFixed(1) },
          { label: "Total credits", value: total.toFixed(1) },
          { label: "Est. cost", value: `$${costUsd}`, highlight: true },
        ].map(({ label, value, highlight }) => (
          <div key={label} className={`rounded-[10px] border p-3 ${highlight ? "border-primary/40 bg-primary/10" : "border-muted-bg bg-secondary"}`}>
            <div className="text-xs text-muted mb-1">{label}</div>
            <div className={`text-xl font-bold ${highlight ? "text-primary" : "text-foreground"}`}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
